// src/pages/DepositsPage.jsx
//
// Gestion des dépôts (collections confiées) et prêts individuels d'œuvres.
// Accessible via /deposits — menu Services > Dépôts & Prêts
//
// Tables utilisées (sans migration) :
//   - collections (collection_type = 'deposit')
//   - deposits    (fiche contrat liée à une collection)
//   - artworks    (loan_status, availability, location_id)
//
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { toast } from "react-hot-toast";
import { AlertTriangle, CheckCircle2, Building2, User, Calendar, FileText, Landmark } from "lucide-react";
import { withoutProposalWorks } from "../lib/proposalExclusion";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const localeFromLanguage = (language) => {
  const lang = String(language || "fr").toLowerCase();
  if (lang.startsWith("de")) return "de-DE";
  if (lang.startsWith("en")) return "en-GB";
  return "fr-FR";
};

const fmt = (n, locale) =>
  n ? new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n) : "—";

const fmtDate = (d, locale) =>
  d ? new Date(d).toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" }) : "—";

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
}

function ContractAlert({ endDate }) {
  const { t, i18n } = useTranslation();
  const locale = localeFromLanguage(i18n.resolvedLanguage || i18n.language);
  const days = daysUntil(endDate);
  if (days === null) return (
    <span style={{ fontSize: 11, color: "#d97706", fontFamily: "system-ui", fontWeight: 600 }}>
      {t("depositsPage.contract.noEndDate")}
    </span>
  );
  if (days < 0) return (
    <span style={{ fontSize: 11, color: "#dc2626", fontFamily: "system-ui", fontWeight: 700,
      background: "#fef2f2", padding: "2px 8px", borderRadius: 3, border: "1px solid #fecaca" }}>
      {t("depositsPage.contract.expiredAgo", { count: Math.abs(days) })}
    </span>
  );
  if (days <= 60) return (
    <span style={{ fontSize: 11, color: "#d97706", fontFamily: "system-ui", fontWeight: 700,
      background: "#fffbeb", padding: "2px 8px", borderRadius: 3, border: "1px solid #fde68a" }}>
      {t("depositsPage.contract.expiresIn", { count: days })}
    </span>
  );
  return (
    <span style={{ fontSize: 11, color: "#16a34a", fontFamily: "system-ui", fontWeight: 600 }}>
      {t("depositsPage.contract.validUntil", { date: fmtDate(endDate, locale) })}
    </span>
  );
}

// ─── Modal édition fiche dépôt ────────────────────────────────────────────────

function DepositModal({ deposit, collectionId, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(
    deposit || {
      owner_type: "a_clarifier",
      owner_name: "",
      owner_contact: "",
      contract_start_date: "",
      contract_end_date: "",
      insurance_required: true,
      insurance_holder: "",
      conditions_notes: "",
    }
  );
  const [saving, setSaving] = useState(false);

  const setF = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (deposit?.id) {
        const { error } = await supabase
          .from("deposits")
          .update({ ...form, updated_at: new Date().toISOString() })
          .eq("id", deposit.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("deposits")
          .insert([{ ...form, collection_id: collectionId }]);
        if (error) throw error;
      }
      toast.success(t("depositsPage.toast.depositSaved"));
      onSaved();
    } catch (err) {
      toast.error(t("depositsPage.toast.error", { message: err.message }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex",
      alignItems: "center", justifyContent: "center", zIndex: 9000 }}>
      <div style={{ background: "#fff", borderRadius: 8, padding: 28, width: "100%", maxWidth: 520,
        maxHeight: "85vh", overflowY: "auto" }}>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 18, marginBottom: 20, color: "#1a1a1a" }}>
          {t("depositsPage.depositModal.title")}
        </h2>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
          <div>
            <label style={sLabel}>{t("depositsPage.depositModal.ownerType")}</label>
            <select value={form.owner_type} onChange={(e) => setF("owner_type", e.target.value)} style={sInput}>
              <option value="a_clarifier">{t("depositsPage.ownerTypes.a_clarifier")}</option>
              <option value="private_collector">{t("depositsPage.ownerTypes.private_collector")}</option>
              <option value="institution">{t("depositsPage.ownerTypes.institution")}</option>
              <option value="family_succession">{t("depositsPage.ownerTypes.family_succession")}</option>
            </select>
          </div>
          <div>
            <label style={sLabel}>{t("depositsPage.depositModal.ownerName")}</label>
            <input value={form.owner_name || ""} onChange={(e) => setF("owner_name", e.target.value)}
              placeholder={t("depositsPage.depositModal.ownerNamePlaceholder")} style={sInput} />
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={sLabel}>{t("depositsPage.depositModal.contact")}</label>
          <input value={form.owner_contact || ""} onChange={(e) => setF("owner_contact", e.target.value)}
            placeholder={t("depositsPage.depositModal.contactPlaceholder")} style={sInput} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
          <div>
            <label style={sLabel}>{t("depositsPage.depositModal.contractStart")}</label>
            <input type="date" value={form.contract_start_date || ""} onChange={(e) => setF("contract_start_date", e.target.value)} style={sInput} />
          </div>
          <div>
            <label style={sLabel}>{t("depositsPage.depositModal.contractEnd")}</label>
            <input type="date" value={form.contract_end_date || ""} onChange={(e) => setF("contract_end_date", e.target.value)} style={sInput} />
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ ...sLabel, display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={!!form.insurance_required}
              onChange={(e) => setF("insurance_required", e.target.checked)}
              style={{ accentColor: "#c9a961" }} />
            {t("depositsPage.depositModal.insuranceRequired")}
          </label>
          {form.insurance_required && (
            <input value={form.insurance_holder || ""} onChange={(e) => setF("insurance_holder", e.target.value)}
              placeholder={t("depositsPage.depositModal.insuranceHolderPlaceholder")} style={{ ...sInput, marginTop: 8 }} />
          )}
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={sLabel}>{t("depositsPage.depositModal.specialConditions")}</label>
          <textarea value={form.conditions_notes || ""} onChange={(e) => setF("conditions_notes", e.target.value)}
            rows={3} placeholder={t("depositsPage.depositModal.specialConditionsPlaceholder")}
            style={{ ...sInput, resize: "vertical" }} />
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={sBtnSecondary}>{t("depositsPage.actions.cancel")}</button>
          <button onClick={handleSave} disabled={saving} style={sBtnPrimary}>
            {saving ? t("depositsPage.actions.saving") : t("depositsPage.actions.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal prêt d'une œuvre ───────────────────────────────────────────────────

function LoanModal({ artwork, onClose, onSaved }) {
  const { t } = useTranslation();
  const isOnLoan = artwork.loan_status === "on_loan" || artwork.availability === "on_loan";
  const [form, setForm] = useState({
    loan_status: isOnLoan ? "none" : "on_loan",
    availability: isOnLoan ? "available" : "on_loan",
    storage_location_detail: artwork.storage_location_detail || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("artworks")
        .update({
          loan_status: form.loan_status,
          availability: form.availability,
          storage_location_detail: form.storage_location_detail,
        })
        .eq("id", artwork.id);
      if (error) throw error;
      toast.success(form.loan_status === "on_loan" ? t("depositsPage.toast.loanMarked") : t("depositsPage.toast.loanRemoved"));
      onSaved();
    } catch (err) {
      toast.error(t("depositsPage.toast.error", { message: err.message }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex",
      alignItems: "center", justifyContent: "center", zIndex: 9000 }}>
      <div style={{ background: "#fff", borderRadius: 8, padding: 28, width: "100%", maxWidth: 440 }}>
        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 18, marginBottom: 6, color: "#1a1a1a" }}>
          {isOnLoan ? t("depositsPage.loanModal.removeTitle") : t("depositsPage.loanModal.markTitle")}
        </h2>
        <p style={{ fontSize: 13, color: "#888", fontFamily: "Georgia, serif", marginBottom: 20, fontStyle: "italic" }}>
          {artwork.title}{artwork.artist ? ` — ${artwork.artist}` : ""}
        </p>

        {!isOnLoan && (
          <div style={{ marginBottom: 16 }}>
            <label style={sLabel}>{t("depositsPage.loanModal.destination")}</label>
            <input
              value={form.storage_location_detail}
              onChange={(e) => setForm((p) => ({ ...p, storage_location_detail: e.target.value }))}
              placeholder={t("depositsPage.loanModal.destinationPlaceholder")}
              style={sInput}
            />
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={sBtnSecondary}>{t("depositsPage.actions.cancel")}</button>
          <button onClick={handleSave} disabled={saving}
            style={{ ...sBtnPrimary, background: isOnLoan ? "#16a34a" : "#1a1a1a" }}>
            {saving ? "…" : isOnLoan ? t("depositsPage.loanModal.confirmReturn") : t("depositsPage.loanModal.confirmLoan")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function DepositsPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [depositCollections, setDepositCollections] = useState([]); // collections type='deposit' + fiche deposits
  const [loanedArtworks, setLoanedArtworks] = useState([]);         // artworks en prêt
  const [unlocatedArtworks, setUnlocatedArtworks] = useState([]);   // artworks sans location_id
  const [editDeposit, setEditDeposit] = useState(null);             // { collection, deposit }
  const [editLoan, setEditLoan] = useState(null);                   // artwork
  const [tab, setTab] = useState("deposits");                       // 'deposits' | 'loans' | 'risks'

  const load = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // 1. Collections en dépôt
      const { data: cols } = await supabase
        .from("collections")
        .select("id, title, collection_type, legal_status")
        .eq("client_id", user.id)
        .eq("collection_type", "deposit");

      // 2. Fiches deposits pour ces collections
      let depositsMap = {};
      if (cols?.length) {
        const colIds = cols.map((c) => c.id);
        const { data: deps } = await supabase
          .from("deposits")
          .select("*")
          .in("collection_id", colIds);
        (deps || []).forEach((d) => { depositsMap[d.collection_id] = d; });
      }

      // Compter les œuvres par collection
      let artworkCountMap = {};
      if (cols?.length) {
        for (const col of cols) {
          const { count } = await supabase
            .from("artworks")
            .select("id", { count: "exact", head: true })
            .eq("collection_id", col.id);
          artworkCountMap[col.id] = count || 0;
        }
      }

      setDepositCollections(
        (cols || []).map((c) => ({
          ...c,
          deposit: depositsMap[c.id] || null,
          artworkCount: artworkCountMap[c.id] || 0,
        }))
      );

      // 3. Œuvres en prêt
      const { data: loans } = await withoutProposalWorks(supabase
        .from("artworks")
        .select("id, title, artist, inventory_number, storage_location_detail, loan_status, availability, location_id, current_estimated_value, collection_id")
        .eq("client_id", user.id)
        .or("loan_status.eq.on_loan,availability.eq.on_loan"), user.id);

      setLoanedArtworks(loans || []);

      // 4. Œuvres sans localisation (risque) — exclure les œuvres en prêt (leur lieu = institution)
      const { data: unlocated } = await withoutProposalWorks(supabase
        .from("artworks")
        .select("id, title, artist, inventory_number, loan_status, availability, collections(title)")
        .eq("client_id", user.id)
        .is("location_id", null)
        .neq("availability", "sold")
        .neq("availability", "donated")
        .neq("availability", "on_loan")
        .neq("loan_status", "on_loan")
        .limit(50), user.id);

      setUnlocatedArtworks(unlocated || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Stats pour les onglets
  const expiredOrUrgent = depositCollections.filter((c) => {
    const days = daysUntil(c.deposit?.contract_end_date);
    return days === null || days < 0 || days <= 60;
  }).length;

  const TABS = [
    { key: "deposits", label: t("depositsPage.tabs.deposits"), count: depositCollections.length },
    { key: "loans", label: t("depositsPage.tabs.loans"), count: loanedArtworks.length },
    { key: "risks", label: t("depositsPage.tabs.risks"), count: unlocatedArtworks.length, alert: unlocatedArtworks.length > 0 },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "40px 24px" }}>

      {/* ── En-tête ── */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 28, color: "#1a1a1a", margin: "0 0 6px" }}>
          {t("depositsPage.title")}
        </h1>
        <p style={{ fontSize: 13, color: "#888", fontFamily: "system-ui", margin: 0 }}>
          {t("depositsPage.subtitle")}
        </p>
      </div>

      {/* ── 2 colonnes : contenu principal + sidebar « dossier musée » ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 28, alignItems: "flex-start" }}>
        <div style={{ flex: "1 1 480px", minWidth: 0 }}>

      {/* ── Onglets ── */}
      <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #e0d8cc", marginBottom: 28 }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: "10px 20px", background: "none", border: "none",
              borderBottom: tab === t.key ? "2px solid #1a1a1a" : "2px solid transparent",
              cursor: "pointer", fontSize: 13, fontFamily: "system-ui",
              color: tab === t.key ? "#1a1a1a" : "#888", fontWeight: tab === t.key ? 600 : 400,
              display: "flex", alignItems: "center", gap: 7,
            }}
          >
            {t.label}
            <span style={{
              fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 10,
              background: t.alert ? "#fef2f2" : "#f0ece5",
              color: t.alert ? "#dc2626" : "#888",
              border: t.alert ? "1px solid #fecaca" : "1px solid #e0d8cc",
            }}>
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#aaa", fontFamily: "Georgia, serif" }}>
          {t("common.loading.safe", { defaultValue: "Chargement…" })}
        </div>
      ) : (
        <>
          {/* ── TAB : Collections en dépôt ── */}
          {tab === "deposits" && (
            <div>
              {depositCollections.length === 0 ? (
                <EmptyState
                  icon="◇"
                  title={t("depositsPage.empty.depositsTitle")}
                  text={t("depositsPage.empty.depositsText")}
                  cta={t("depositsPage.empty.viewCollections")}
                  to="/collections"
                />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {depositCollections.map((col) => (
                    <DepositCard
                      key={col.id}
                      col={col}
                      onEdit={() => setEditDeposit({ collection: col, deposit: col.deposit })}
                    />
                  ))}
                </div>
              )}

              {/* Alerte contrats urgents */}
              {expiredOrUrgent > 0 && (
                <div style={{
                  marginTop: 20, padding: "14px 18px",
                  background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6,
                  display: "flex", alignItems: "center", gap: 10,
                }}>
                  <AlertTriangle size={16} color="#dc2626" />
                  <span style={{ fontSize: 13, color: "#dc2626", fontFamily: "system-ui" }}>
                    {t("depositsPage.contract.urgentSummary", { count: expiredOrUrgent })}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ── TAB : Œuvres en prêt ── */}
          {tab === "loans" && (
            <div>
              {loanedArtworks.length === 0 ? (
                <EmptyState
                  icon="↗"
                  title={t("depositsPage.empty.loansTitle")}
                  text={t("depositsPage.empty.loansText")}
                  cta={t("depositsPage.empty.viewCollection")}
                  to="/collections"
                />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {loanedArtworks.map((artwork) => (
                    <LoanCard
                      key={artwork.id}
                      artwork={artwork}
                      onEdit={() => setEditLoan(artwork)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── TAB : À clarifier ── */}
          {tab === "risks" && (
            <div>
              {unlocatedArtworks.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40 }}>
                  <CheckCircle2 size={32} color="#16a34a" style={{ margin: "0 auto 12px" }} />
                  <p style={{ fontFamily: "Georgia, serif", fontSize: 16, color: "#1a1a1a" }}>
                    {t("depositsPage.risks.allLocated")}
                  </p>
                  <p style={{ fontFamily: "Georgia, serif", fontSize: 13, color: "#888", fontStyle: "italic", marginTop: 6 }}>
                    {t("depositsPage.risks.loanLocatedHint")}
                  </p>
                </div>
              ) : (
                <>
                <div style={{
                  padding: "14px 18px", background: "#fffbeb",
                  border: "1px solid #fde68a", borderRadius: 6, marginBottom: 20,
                  fontSize: 13, color: "#78350f", fontFamily: "system-ui",
                }}>
                  <strong>{t("depositsPage.risks.unknownLocationTitle")}</strong> {t("depositsPage.risks.unknownLocationText")}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {unlocatedArtworks.map((artwork) => (
                    <div key={artwork.id} style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "10px 14px", background: "#fafaf8",
                      border: "1px solid #f0ece5", borderRadius: 5,
                    }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: 4 }}>
                          <span style={{ fontFamily: "Georgia, serif", fontSize: 13, color: "#1a1a1a" }}>
                            {artwork.title || t("common.untitled")}
                          </span>
                          {artwork.artist && (
                            <span style={{ fontSize: 12, color: "#888" }}>— {artwork.artist}</span>
                          )}
                          {artwork.inventory_number && (
                            <span style={{ fontSize: 11, color: "#bbb" }}>[{artwork.inventory_number}]</span>
                          )}
                        </div>
                        {artwork.collections?.title && (
                          <div style={{ fontSize: 11, color: "#c9a961", fontFamily: "system-ui", marginTop: 2 }}>
                            {t("depositsPage.risks.collectionLabel", { collection: artwork.collections.title })}
                          </div>
                        )}
                      </div>
                      <Link
                        to={`/artwork/${artwork.id}`}
                        style={{ fontSize: 12, color: "#c9a961", fontFamily: "system-ui", fontWeight: 600, textDecoration: "none" }}
                      >
                        {t("depositsPage.actions.complete")} →
                      </Link>
                    </div>
                  ))}
                </div>
                </>
              )}
            </div>
          )}
        </>
      )}

        </div>{/* fin contenu principal */}

        {/* ── Sidebar : dossier de proposition musée ── */}
        <aside style={{ flex: "1 1 240px", minWidth: 240, maxWidth: 300 }}>
          <div style={{ background: "#faf6ec", border: "1px solid #e6d9b3", borderRadius: 14, padding: 20, position: "sticky", top: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Landmark size={18} style={{ color: "#a9843f" }} aria-hidden="true" />
              <h3 style={{ fontFamily: "Georgia, serif", fontSize: 16, margin: 0, color: "#1a1a1a" }}>
                {t("depositsPage.museumSidebar.title", { defaultValue: "Proposer à un musée" })}
              </h3>
            </div>
            <p style={{ fontSize: 13, color: "#6b6459", lineHeight: 1.5, margin: "0 0 14px" }}>
              {t("depositsPage.museumSidebar.text", {
                defaultValue:
                  "Constituez un dossier de proposition pour une institution à partir de vos œuvres.",
              })}
            </p>
            <Link
              to="/propose"
              style={{
                display: "inline-block", background: "#1a1a1a", color: "#fff",
                fontSize: 13, fontWeight: 500, padding: "10px 16px", borderRadius: 12,
                textDecoration: "none",
              }}
            >
              {t("depositsPage.museumSidebar.cta", { defaultValue: "Créer un dossier musée" })}
            </Link>
          </div>
        </aside>
      </div>{/* fin 2 colonnes */}

      {/* ── Modals ── */}
      {editDeposit && (
        <DepositModal
          deposit={editDeposit.deposit}
          collectionId={editDeposit.collection.id}
          onClose={() => setEditDeposit(null)}
          onSaved={() => { setEditDeposit(null); load(); }}
        />
      )}
      {editLoan && (
        <LoanModal
          artwork={editLoan}
          onClose={() => setEditLoan(null)}
          onSaved={() => { setEditLoan(null); load(); }}
        />
      )}
    </div>
  );
}

// ─── Sous-composants ──────────────────────────────────────────────────────────

function DepositCard({ col, onEdit }) {
  const { t, i18n } = useTranslation();
  const locale = localeFromLanguage(i18n.resolvedLanguage || i18n.language);
  const dep = col.deposit;
  const days = dep ? daysUntil(dep.contract_end_date) : null;
  const isUrgent = days === null || (days !== null && days <= 60);

  return (
    <div style={{
      border: isUrgent ? "1px solid #fecaca" : "1px solid #e0d8cc",
      borderRadius: 8, background: "#fff", overflow: "hidden",
    }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
        padding: "16px 20px",
        borderBottom: dep ? "1px solid #f0ece5" : "none",
        background: isUrgent ? "#fff8f8" : "#fff",
      }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <span style={{ fontFamily: "Georgia, serif", fontSize: 16, color: "#1a1a1a" }}>
              {col.title}
            </span>
            <span style={{
              fontSize: 10, fontFamily: "system-ui", fontWeight: 700,
              padding: "2px 7px", borderRadius: 2, textTransform: "uppercase",
              background: "#1a1a1a", color: "#fff",
            }}>
              {t("depositsPage.depositCard.badge")}
            </span>
          </div>
          <div style={{ fontSize: 12, color: "#888", fontFamily: "system-ui" }}>
            {t("depositsPage.depositCard.artworkCount", { count: col.artworkCount })}
            {dep?.owner_name && <span> · {dep.owner_name}</span>}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {dep && <ContractAlert endDate={dep.contract_end_date} />}
          <button onClick={onEdit} style={{
            fontSize: 12, color: "#c9a961", fontFamily: "system-ui",
            fontWeight: 600, background: "none", border: "none", cursor: "pointer",
          }}>
            {dep ? t("depositsPage.actions.edit") : t("depositsPage.actions.complete")}
          </button>
          <Link to={`/collections/${col.id}`} style={{
            fontSize: 12, color: "#888", fontFamily: "system-ui", textDecoration: "none",
          }}>
            {t("depositsPage.actions.view")} →
          </Link>
        </div>
      </div>

      {dep && (
        <div style={{ padding: "12px 20px", display: "flex", flexWrap: "wrap", gap: 16 }}>
          {dep.owner_type && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Building2 size={12} color="#aaa" />
              <span style={{ fontSize: 12, color: "#555", fontFamily: "system-ui" }}>
                {t(`depositsPage.ownerTypes.${dep.owner_type}`, { defaultValue: dep.owner_type })}
              </span>
            </div>
          )}
          {dep.owner_contact && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <User size={12} color="#aaa" />
              <span style={{ fontSize: 12, color: "#555", fontFamily: "system-ui" }}>{dep.owner_contact}</span>
            </div>
          )}
          {dep.contract_start_date && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Calendar size={12} color="#aaa" />
              <span style={{ fontSize: 12, color: "#555", fontFamily: "system-ui" }}>
                {fmtDate(dep.contract_start_date, locale)} → {fmtDate(dep.contract_end_date, locale)}
              </span>
            </div>
          )}
          {dep.insurance_required && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <FileText size={12} color="#aaa" />
              <span style={{ fontSize: 12, color: "#555", fontFamily: "system-ui" }}>
                {t("depositsPage.depositCard.insurance", { holder: dep.insurance_holder || t("depositsPage.depositCard.toDefine") })}
              </span>
            </div>
          )}
          {dep.conditions_notes && (
            <div style={{ fontSize: 11, color: "#888", fontStyle: "italic", fontFamily: "Georgia, serif", width: "100%" }}>
              {dep.conditions_notes}
            </div>
          )}
        </div>
      )}

      {!dep && (
        <div style={{ padding: "10px 20px", background: "#fffbeb", borderTop: "1px solid #fde68a" }}>
          <span style={{ fontSize: 12, color: "#92400e", fontFamily: "system-ui" }}>
            {t("depositsPage.depositCard.missingRecord")}
          </span>
        </div>
      )}
    </div>
  );
}

function LoanCard({ artwork, onEdit }) {
  const { t, i18n } = useTranslation();
  const locale = localeFromLanguage(i18n.resolvedLanguage || i18n.language);
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "12px 16px", background: "#fff",
      border: "1px solid #e0d8cc", borderRadius: 6,
    }}>
      <div style={{ flex: 1 }}>
        <span style={{ fontFamily: "Georgia, serif", fontSize: 14, color: "#1a1a1a" }}>
          {artwork.title || t("common.untitled")}
        </span>
        {artwork.artist && (
          <span style={{ fontSize: 13, color: "#888", marginLeft: 8 }}>— {artwork.artist}</span>
        )}
        {artwork.inventory_number && (
          <span style={{ fontSize: 11, color: "#bbb", marginLeft: 8 }}>[{artwork.inventory_number}]</span>
        )}
        {artwork.storage_location_detail && (
          <div style={{ fontSize: 12, color: "#c9a961", fontFamily: "system-ui", marginTop: 2 }}>
            ↗ {artwork.storage_location_detail}
          </div>
        )}
        {artwork.current_estimated_value && (
          <div style={{ fontSize: 11, color: "#aaa", fontFamily: "system-ui", marginTop: 1 }}>
            {t("depositsPage.loanCard.estimatedValue", { value: fmt(artwork.current_estimated_value, locale) })}
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
        <button onClick={onEdit} style={{
          fontSize: 12, color: "#16a34a", fontFamily: "system-ui",
          fontWeight: 600, background: "none", border: "none", cursor: "pointer",
        }}>
          {t("depositsPage.loanCard.return")}
        </button>
        <Link to={`/artwork/${artwork.id}`} style={{
          fontSize: 12, color: "#888", fontFamily: "system-ui", textDecoration: "none",
        }}>
          {t("depositsPage.loanCard.record")} →
        </Link>
      </div>
    </div>
  );
}

function EmptyState({ icon, title, text, cta, to }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 24px" }}>
      <div style={{ fontFamily: "Georgia, serif", fontSize: 36, color: "#e0d8cc", marginBottom: 16 }}>
        {icon}
      </div>
      <p style={{ fontFamily: "Georgia, serif", fontSize: 17, color: "#1a1a1a", marginBottom: 8 }}>
        {title}
      </p>
      <p style={{ fontSize: 13, color: "#888", fontFamily: "system-ui", marginBottom: 20, maxWidth: 360, margin: "0 auto 20px" }}>
        {text}
      </p>
      {cta && to && (
        <Link to={to} style={{
          display: "inline-block", padding: "9px 20px",
          background: "#1a1a1a", color: "#fff",
          fontSize: 13, fontFamily: "system-ui", fontWeight: 600,
          textDecoration: "none", borderRadius: 4,
        }}>
          {cta}
        </Link>
      )}
    </div>
  );
}

// ─── Styles communs ───────────────────────────────────────────────────────────

const sLabel = {
  display: "block", fontSize: 11, color: "#888",
  fontFamily: "system-ui", marginBottom: 5,
};
const sInput = {
  width: "100%", boxSizing: "border-box",
  border: "1px solid #e0d8cc", borderRadius: 4,
  padding: "8px 10px", fontSize: 13,
  fontFamily: "Georgia, serif", color: "#1a1a1a",
  outline: "none", background: "#fff",
};
const sBtnPrimary = {
  padding: "9px 18px", background: "#1a1a1a", color: "#fff",
  border: "none", borderRadius: 4, fontSize: 13,
  fontFamily: "system-ui", fontWeight: 600, cursor: "pointer",
};
const sBtnSecondary = {
  padding: "9px 18px", background: "#fff", color: "#555",
  border: "1px solid #e0d8cc", borderRadius: 4, fontSize: 13,
  fontFamily: "system-ui", fontWeight: 600, cursor: "pointer",
};